import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { getInputClasses } from "@utils/get-input-classes";
import { useAuthStore } from "@stores/auth";
import { getAll as getAllHospitals } from "@services/hospitals";
import { storeHelpRequest } from "@services/help-requests";
import AlertError from "@components/shared/alert-error";

export const REQUEST_TYPES = {
  INCIDENCIA: "Incidencia",
  ASESORIA: "Asesoría",
};

export const USER_TYPES = ["Hospital", "Aseguradora"];

export const INCIDENT_DETAIL_OPTIONS = [
  "No puedo Cargar Documentación en el Sistema",
  "No puedo Aperturar un Registro en el Sistema",
  "No puedo enviar mi Tramite a Mesa de Control",
  "Mi Folio se encuentra Finalizado",
  "No puedo activar un Tramite como Extemporáneo",
  "Otro",
];

export const MODULES_BY_USER_TYPE = {
  Hospital: [
    "Cirugías programadas",
    "Ingreso Hospitalario",
    "Seguimiento",
    "Informacion Medica",
    "Actualizacion de Tramite",
    "Alta Paciente",
    "Consulta General",
    "Solicitudes Extemporáneas",
    "Seguimiento Extrahospitalario",
  ],
  Aseguradora: [
    "Mesa de Control",
    "Dictamen Medico",
    "Control de Costos",
    "24/7 Contigo",
    "Consulta General",
    "Dictamen Extemporáneo",
    "Supervision Hospitalaria",
    "Solicitudes",
    "Casos finalizados",
    "Cancelaciones",
    "Programaciones",
    "Solicitudes Registros Medicos",
  ],
};

export const validationSchema = Yup.object().shape({
  requestType: Yup.string().required(),
  hospitalId: Yup.string().when("userType", {
    is: "Hospital",
    then: Yup.string().required("El campo es requerido"),
  }),
  userType: Yup.string().required("El campo es requerido"),
  folioAtcPlus: Yup.string().when("requestType", {
    is: REQUEST_TYPES.INCIDENCIA,
    then: Yup.string().max(50).required("El campo es requerido"),
  }),
  module: Yup.string().when("requestType", {
    is: REQUEST_TYPES.INCIDENCIA,
    then: Yup.string().max(50).required("El campo es requerido"),
  }),
  incidentDetail: Yup.string().when("requestType", {
    is: REQUEST_TYPES.INCIDENCIA,
    then: Yup.string().max(200).required("El campo es requerido"),
  }),
  description: Yup.string().trim().min(4).max(4000).required("El campo es requerido"),
  contactName: Yup.string().trim().max(120).required("El campo es requerido"),
  contactEmail: Yup.string()
    .trim()
    .email("Correo inválido")
    .max(150)
    .required("El campo es requerido"),
  contactAlsoByPhone: Yup.boolean(),
  contactPhone: Yup.string().when("contactAlsoByPhone", {
    is: true,
    then: Yup.string().matches(/^\d{10}$/, "Escribe 10 dígitos").required("El campo es requerido"),
  }),
});

const HelpRequestModal = ({ show, onHide }) => {
  const user = useAuthStore((state) => state.user);
  const [hospitals, setHospitals] = useState([]);

  useEffect(() => {
    if (show) {
      getAllHospitals().then((response) => setHospitals(response.data || []));
    }
  }, [show]);

  const initialValues = {
    requestType: REQUEST_TYPES.INCIDENCIA,
    hospitalId: user?.hospital?.id || "",
    folioAtcPlus: "",
    userType: user?.userType || "Hospital",
    module: "",
    incidentDetail: "",
    description: "",
    contactName: user?.name || user?.userName || "",
    contactEmail: user?.email || "",
    contactAlsoByPhone: false,
    contactPhone: "",
  };

  const handleSubmit = (values, formikBag) => {
    return storeHelpRequest(values, hospitals)
      .then(() => {
        formikBag.resetForm();
        onHide();
      })
      .catch((error) => {
        formikBag.setStatus(
          error?.response?.data?.message ||
            "Ocurrió un error al enviar la solicitud, intenta de nuevo."
        );
      })
      .finally(() => formikBag.setSubmitting(false));
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>¿En qué te ayudamos?</Modal.Title>
      </Modal.Header>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          status,
          handleChange,
          setFieldValue,
          handleSubmit: formikHandleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={formikHandleSubmit}>
            <Modal.Body>
              <AlertError status={status} />

              <Form.Group>
                <Form.Label>Tipo de Solicitud</Form.Label>
                <div className="d-flex">
                  <Button
                    className="mr-2"
                    variant={
                      values.requestType === REQUEST_TYPES.INCIDENCIA
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => setFieldValue("requestType", REQUEST_TYPES.INCIDENCIA)}
                  >
                    Incidencia
                  </Button>
                  <Button
                    variant={
                      values.requestType === REQUEST_TYPES.ASESORIA
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => setFieldValue("requestType", REQUEST_TYPES.ASESORIA)}
                  >
                    Asesoría
                  </Button>
                </div>
              </Form.Group>

              <Form.Group controlId="hospitalId">
                <Form.Label>Hospital</Form.Label>
                <Form.Control
                  as="select"
                  name="hospitalId"
                  value={values.hospitalId}
                  onChange={handleChange}
                  className={`${getInputClasses(touched, errors, "hospitalId")}`}
                >
                  <option value="">SELECCIONA UNA OPCIÓN...</option>
                  {hospitals?.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.hospitalName}
                    </option>
                  ))}
                </Form.Control>
                <ErrorMessage name="hospitalId">
                  {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                </ErrorMessage>
              </Form.Group>

              {values.requestType === REQUEST_TYPES.INCIDENCIA && (
                <Form.Group controlId="folioAtcPlus">
                  <Form.Label>Folio ATC Plus</Form.Label>
                  <Form.Control
                    type="text"
                    name="folioAtcPlus"
                    value={values.folioAtcPlus}
                    onChange={handleChange}
                    className={`${getInputClasses(touched, errors, "folioAtcPlus")}`}
                  />
                  <ErrorMessage name="folioAtcPlus">
                    {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                  </ErrorMessage>
                </Form.Group>
              )}

              <Form.Group controlId="userType">
                <Form.Label>Tipo de Usuario</Form.Label>
                <Form.Control
                  as="select"
                  name="userType"
                  value={values.userType}
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue("module", "");
                  }}
                  className={`${getInputClasses(touched, errors, "userType")}`}
                >
                  {USER_TYPES.map((userType) => (
                    <option key={userType} value={userType}>
                      {userType}
                    </option>
                  ))}
                </Form.Control>
                <ErrorMessage name="userType">
                  {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                </ErrorMessage>
              </Form.Group>

              {values.requestType === REQUEST_TYPES.INCIDENCIA && (
                <>
                  <Form.Group controlId="module">
                    <Form.Label>Módulo en el que se está presentando la Incidencia</Form.Label>
                    <Form.Control
                      as="select"
                      name="module"
                      value={values.module}
                      onChange={handleChange}
                      className={`${getInputClasses(touched, errors, "module")}`}
                    >
                      <option value="">SELECCIONA UNA OPCIÓN...</option>
                      {(MODULES_BY_USER_TYPE[values.userType] || []).map((module) => (
                        <option key={module} value={module}>
                          {module}
                        </option>
                      ))}
                    </Form.Control>
                    <ErrorMessage name="module">
                      {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                    </ErrorMessage>
                  </Form.Group>

                  <Form.Group controlId="incidentDetail">
                    <Form.Label>Detalle Incidencia</Form.Label>
                    <Form.Control
                      as="select"
                      name="incidentDetail"
                      value={values.incidentDetail}
                      onChange={handleChange}
                      className={`${getInputClasses(touched, errors, "incidentDetail")}`}
                    >
                      <option value="">SELECCIONA UNA OPCIÓN...</option>
                      {INCIDENT_DETAIL_OPTIONS.map((detail) => (
                        <option key={detail} value={detail}>
                          {detail}
                        </option>
                      ))}
                    </Form.Control>
                    <ErrorMessage name="incidentDetail">
                      {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                    </ErrorMessage>
                  </Form.Group>
                </>
              )}

              <Form.Group controlId="description">
                <Form.Label>Breve Explicación</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  className={`${getInputClasses(touched, errors, "description")}`}
                />
                <ErrorMessage name="description">
                  {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <Form.Label>Medio de Contacto</Form.Label>
              <Form.Group controlId="contactName">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="contactName"
                  value={values.contactName}
                  onChange={handleChange}
                  className={`${getInputClasses(touched, errors, "contactName")}`}
                />
                <ErrorMessage name="contactName">
                  {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                </ErrorMessage>
              </Form.Group>
              <Form.Group controlId="contactEmail">
                <Form.Label>Correo electrónico</Form.Label>
                <Form.Control
                  type="email"
                  name="contactEmail"
                  value={values.contactEmail}
                  onChange={handleChange}
                  className={`${getInputClasses(touched, errors, "contactEmail")}`}
                />
                <ErrorMessage name="contactEmail">
                  {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <Form.Group controlId="contactAlsoByPhone">
                <Form.Check
                  type="checkbox"
                  label="También por teléfono"
                  name="contactAlsoByPhone"
                  checked={values.contactAlsoByPhone}
                  onChange={handleChange}
                />
              </Form.Group>

              {values.contactAlsoByPhone && (
                <Form.Group controlId="contactPhone">
                  <Form.Control
                    type="text"
                    placeholder="Ej. 5512345678"
                    name="contactPhone"
                    value={values.contactPhone}
                    onChange={handleChange}
                    className={`${getInputClasses(touched, errors, "contactPhone")}`}
                  />
                  <ErrorMessage name="contactPhone">
                    {(msg) => <Form.Control.Feedback type="invalid">{msg}</Form.Control.Feedback>}
                  </ErrorMessage>
                </Form.Group>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Spinner as="span" animation="border" size="sm" className="mr-2" />
                )}
                Enviar solicitud
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default HelpRequestModal;
